import useAiSharing from "./useAiSharing";
import OpportunityBank from "@/components/OpportunityBank";
import HistorySources from './HistorySources';
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  RefreshCw, MousePointerClick, Eye, LogOut,
  Percent, TrendingUp, Loader2, Info, Rocket, AlertCircle,
  PlayCircle, ArrowRight, LayoutGrid, ChevronLeft, RotateCcw,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DashboardRail, ThemeControl, useDashboardTheme } from "@/components/dashboard/DashboardTheme";
import DashboardReports from "@/components/dashboard/DashboardReports";
import BingReport from "@/components/dashboard/BingReport";
import ClarityReport from "@/components/dashboard/ClarityReport";
import { formatMetric, reportingPeriod, reportAvailable } from "@/lib/dashboardReports.mjs";
import InsightCard from "@/components/InsightCard";
import QuickWinsTable from "@/components/QuickWinsTable";
import ChatPanel from "@/components/ChatPanel";
import Overview from "@/components/Overview";
import SetupGuide from "@/components/SetupGuide";
import SettingsModal from "@/components/SettingsModal";
import AccountMenu from "@/components/AccountMenu";
import FixTracker from "@/components/FixTracker";
import AlertsBell from "@/components/AlertsBell";
import {
  ChartCard, ClicksImpressionsTrend, TrafficTrend, ConversionBars,
  IndexingPanel, DataTable,
} from "@/components/DashCharts";
import {
  fetchProperties, fetchDashboard, fetchInsights, clearToken, logout, workflowRequest,
  fetchGa4Properties, setGa4Property, disconnectGoogle,
  createFix, fetchFixes, updateFix, deleteFix, getMe, setPreferredModel,
} from "@/lib/api";
import { useHeadMeta } from "@/lib/head";
import { activeFixture } from "@/lib/fixtures";
import { MODEL_LABELS } from "@/lib/models.mjs";
import { createRequestGate, sameDashboardContext, pairingRefreshContext, loadDashboardSites } from "@/lib/dashboardRequests.mjs";

const MODEL_LABEL = MODEL_LABELS;

const StatTile = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-card border border-line bg-white p-4">
    <div className="flex items-center gap-2 text-slate-600">
      <Icon size={15} aria-hidden="true" />
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em]">{label}</span>
    </div>
    <div className="tabular mt-2 font-heading text-2xl font-bold tracking-tight text-navy-950">{value}</div>
    {sub && <div className="tabular mt-0.5 text-caption text-slate-600">{sub}</div>}
  </div>
);

const RANGES = [
  { v: 7, label: "7 days" },
  { v: 28, label: "28 days" },
  { v: 90, label: "90 days" },
];

const RangePicker = ({ value, onChange }) => (
  <div data-testid="range-picker" role="group" aria-label="Date range" className="inline-flex items-center rounded-btn border border-line bg-white p-1">
    {RANGES.map((r) => (
      <button
        key={r.v}
        type="button"
        onClick={() => onChange(r.v)}
        aria-pressed={value === r.v}
        data-testid={`range-option-${r.v}`}
        className={`tap-safe rounded-[6px] px-3 text-small font-semibold transition-colors duration-control ease-editorial focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 ${
          value === r.v ? "bg-navy-950 text-white" : "text-slate-600 hover:text-navy-950"
        }`}
      >
        <span className="hidden sm:inline">Last </span>{r.label}
      </button>
    ))}
  </div>
);

/** Section heading with the emerald rule, used between major dashboard blocks. */
const SectionHeading = ({ children, right }) => (
  <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
    <div className="flex items-center gap-2">
      <div className="h-6 w-1 rounded-full bg-emerald-600" aria-hidden="true" />
      <h2 className="font-heading text-h2-compact text-navy-950">{children}</h2>
    </div>
    {right}
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const dashboardTheme = useDashboardTheme();
  // Route-specific title, plus a noindex directive so the authenticated
  // dashboard stays out of search results. The authoritative control is the
  // X-Robots-Tag response header, which remains outstanding hosting work and is
  // documented in docs/deployment-seo-requirements.md.
  useHeadMeta({
    title: "Your dashboard — SEO Signals",
    robots: "noindex, nofollow",
  });
  // Dev-only. `activeFixture()` returns null in a production build, so every
  // branch reading it is dead code the minifier removes. See lib/fixtures.js.
  const fixture = activeFixture();

  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [propertiesError, setPropertiesError] = useState(false);
  const [propertiesAttempt, setPropertiesAttempt] = useState(0);
  const [workflowEnabled, setWorkflowEnabled] = useState(null);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingInsights, setLoadingInsights] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [range, setRange] = useState(28);
  const [bingStatus, setBingStatus] = useState("Loading");
  const [clarityStatus, setClarityStatus] = useState("Loading");
  const [model, setModel] = useState(() => localStorage.getItem("sp_model") || "claude");
  const [view, setView] = useState("site");
  const [ga4Options, setGa4Options] = useState([]);
  const [ga4Demo, setGa4Demo] = useState(false);
  const [savingGa4, setSavingGa4] = useState(false);
  const [fixes, setFixes] = useState([]);
  const [trackingIdx, setTrackingIdx] = useState(null);
  const [fixBusyId, setFixBusyId] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const dataGate = useRef(createRequestGate());
  const insightGate = useRef(createRequestGate());
  const contextRef = useRef(null);
  contextRef.current = { selected, range, model };
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const isDemo = typeof window !== "undefined" && localStorage.getItem("sp_demo") === "1";

  useEffect(() => {
    let cancelled = false;
    setPropertiesLoading(true);
    setPropertiesError(false);
    loadDashboardSites(fetchProperties).then(result => {
      if (cancelled) return;
      if (result.status === 'expired') { clearToken(); navigate('/'); return; }
      if (result.status === 'error') { setPropertiesError(true); }
      else {
        setProperties(result.properties);
        setSelected(result.selected);
        setView(result.view);
        if (!result.selected) { setLoadingData(false); setLoadingInsights(false); }
      }
      setPropertiesLoading(false);
    });
    return () => { cancelled = true; };
  }, [navigate, propertiesAttempt]);

  useEffect(() => {
    getMe()
      .then((m) => { setWorkflowEnabled(m.release_workflow_enabled === true); if (m.preferred_model) { setModel(m.preferred_model); localStorage.setItem("sp_model", m.preferred_model); } })
      .catch(() => {});
    fetchGa4Properties()
      .then((d) => { setGa4Options(d.ga4_properties || []); setGa4Demo(!!d.demo); })
      .catch(() => {});
  }, [navigate]);

  const openSite = (id) => { setSelected(id); setView("site"); };

  const handleSetGa4 = async (ga4Property) => {
    if (!selected) return;
    const savedSite = selected;
    setSavingGa4(true);
    try {
      const res = await setGa4Property(savedSite, ga4Property);
      // Saving changes the site's pairing, even if range/model changed while
      // the mutation awaited. Refresh the CURRENT context for that same site.
      const context = pairingRefreshContext(savedSite, contextRef.current);
      if (!context) return;
      const ticket = dataGate.current.begin();
      const current = () => dataGate.current.isCurrent(ticket) && sameDashboardContext(context, contextRef.current);
      insightGate.current.invalidate();
      setData(null);
      setInsights([]);
      setGeneratedAt(null);
      setError(null);
      setLoadingData(true);
      setLoadingInsights(true);
      setRefreshing(false);
      try {
        const d = await fetchDashboard(context.selected, context.range);
        if (!current()) return;
        setData(d);
        setLoadingInsights(false);
        if (current()) toast.success(res.demo ? "GA4 property saved (demo data stays fixed)" : "GA4 property updated. Data refreshed.");
      } catch (e) {
        if (!current()) return;
        setData(null);
        setError("GA4 property saved, but we couldn't refresh this site's data. Please try again.");
      } finally {
        if (current()) {
          setLoadingData(false);
          setLoadingInsights(false);
        }
      }
    } catch (e) {
      if (contextRef.current?.selected === savedSite) toast.error("Couldn't update the GA4 property.");
    } finally {
      setSavingGa4(false);
    }
  };

  const loadFixes = useCallback(async (propId) => {
    try {
      const result = await fetchFixes(propId);
      if (selectedRef.current === propId) setFixes(result);
    } catch (e) { if (selectedRef.current === propId) setFixes([]); }
  }, []);

  const handleTrackFix = async (insight, idx) => {
    if (!selected) return;
    setTrackingIdx(idx);
    try {
      await createFix({
        property_id: selected, headline: insight.headline,
        action: insight.action, evidence: insight.evidence, range,
      });
      await loadFixes(selected);
      toast.success("Tracking this fix — we'll watch the numbers");
    } catch (e) {
      toast.error("Couldn't track this fix.");
    } finally {
      setTrackingIdx(null);
    }
  };

  const handleToggleDone = async (fix) => {
    setFixBusyId(fix.id);
    try {
      await updateFix(fix.id, fix.status === "done" ? "tracking" : "done");
      await loadFixes(selected);
    } catch (e) { toast.error("Couldn't update the fix."); }
    finally { setFixBusyId(null); }
  };

  const handleRemoveFix = async (fix) => {
    setFixBusyId(fix.id);
    try {
      await deleteFix(fix.id);
      setFixes((fs) => fs.filter((f) => f.id !== fix.id));
    } catch (e) { toast.error("Couldn't remove the fix."); }
    finally { setFixBusyId(null); }
  };

  const sharing = useAiSharing(selected, selected, model, isDemo);

  const loadInsights = useCallback(async (propId, refresh, rangeDays, modelKey) => {
    if (!await sharing.request()) { setLoadingInsights(false); return; }
    if (workflowEnabled !== false) { setLoadingInsights(false); return; }
    const ticket = insightGate.current.begin();
    const context = { selected: propId, range: rangeDays, model: modelKey };
    const current = () => insightGate.current.isCurrent(ticket) && sameDashboardContext(context, contextRef.current);
    setLoadingInsights(true);
    try {
      const res = await fetchInsights(propId, refresh, rangeDays, modelKey);
      if (!current()) return;
      setInsights(res.insights || []);
      setGeneratedAt(res.generated_at);
      return true;
    } catch (e) {
      if (!current()) return;
      setInsights([]);
    } finally {
      if (current()) setLoadingInsights(false);
    }
  }, [workflowEnabled, sharing]);

  useEffect(() => {
    if (!selected || view !== "site") return;
    const requests = dataGate.current;
    const insightRequests = insightGate.current;
    const ticket = requests.begin();
    const context = { selected, range, model };
    const current = () => requests.isCurrent(ticket) && sameDashboardContext(context, contextRef.current);
    setLoadingData(true);
    setData(null);
    setInsights([]);
    setFixes([]);
    setError(null);
    (async () => {
      try {
        if (fixture === "api-error") throw new Error("fixture");
        const d = await fetchDashboard(selected, range);
        if (!current()) return;
        setData(d);
      } catch (e) {
        if (!current()) return;
        setError("We couldn't fetch this site's data right now. Please try again.");
        setData(null);
      } finally {
        if (current()) setLoadingData(false);
      }
    })();
    setLoadingInsights(false);
    loadFixes(selected);
    return () => { requests.invalidate(); insightRequests.invalidate(); };
  }, [selected, range, view, model, attempt, fixture, loadFixes]);

  const handleRefresh = async () => {
    if (!selected) return;
    setRefreshing(true);
    const refreshed = await loadInsights(selected, true, range, model);
    setRefreshing(false);
    if (refreshed) toast.success("Insights refreshed");
  };

  const handleModelChange = (m) => {
    setModel(m);
    localStorage.setItem("sp_model", m);
    setPreferredModel(m).catch(() => {});
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/");
  };

  const handleDisconnect = async () => {
    if (isDemo) return handleLogout();
    try {
      const result = await disconnectGoogle();
      await logout();
      toast.success('Google disconnected from SEO Signals');
      if (result.provider_revocation === 'unconfirmed') toast.info('Remove SEO Signals from your Google account permissions to also revoke the Google grant.');
      navigate('/');
    } catch {
      toast.error("Couldn't disconnect Google. Please try again; you are still signed in.");
    }
  };

  const meta = data?.property;
  const isNew = fixture === "low-data" || (meta ? meta.connected_days < 7 : false);
  const gsc = data?.gsc;
  const ga4 = data?.ga4;

  // Fixture overrides. Dev only.
  const showInsightsSkeleton = loadingInsights || fixture === "loading";
  const shownInsights = fixture === "empty-insights" || fixture === "loading" ? [] : [
    ...(data?.opportunity_insights || []).map(insight => ({ ...insight, source: "Search Console" })),
    ...insights,
  ].slice(0, 3);
  const showChartsSkeleton = loadingData || fixture === "loading";
  const shownIndexing = fixture === "indexing-unavailable" ? { unavailable: true } : gsc?.indexing;
  const shownQuickWins = fixture === "empty-insights" || fixture === "loading" ? [] : data?.quick_wins || [];

  return (
    <div className="ss-dashboard min-h-screen bg-offwhite pb-24 font-sans text-navy-950">
      <a href="#dashboard-main" className="ss-skip">Skip to dashboard</a>
      <DashboardRail property={meta} overview={view === "overview"} onOverview={() => setView("overview")} onSite={event => { if (!selected) { event?.preventDefault(); setView("overview"); return; } setView("site"); }} onSetup={() => setSetupOpen(true)} />
      <div className="ss-workspace">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-line bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex min-h-16 flex-wrap py-2 max-w-7xl items-center justify-between gap-4 px-4 lg:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <span className="ss-topbar-title">Your workspace</span>
            {view === "site" && properties.length > 1 && (
              <button
                type="button"
                onClick={() => setView("overview")}
                data-testid="back-to-overview-button"
                className="tap-safe hidden items-center gap-1.5 rounded-btn border border-line px-3 text-small font-medium text-slate-600 transition-colors duration-control ease-editorial hover:bg-offwhite focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 sm:inline-flex"
              >
                <ChevronLeft size={15} aria-hidden="true" /> All sites
              </button>
            )}
          </div>
          {/*
            Three controls, in frequency order: which site, what needs me,
            everything else. AI settings, Google setup and disconnect moved into
            AccountMenu and kept their original testids.
          */}
          <div className="flex flex-wrap justify-end items-center gap-2">
            <Button variant="quiet" size="control" onClick={handleLogout} data-testid="dashboard-logout"><LogOut size={16} aria-hidden="true" /> Log out</Button>
            {view === "site" && properties.length > 0 && (
              <Select value={selected || ""} onValueChange={setSelected}>
                <SelectTrigger data-testid="property-switcher" className="tap-safe w-[150px] border-line bg-white font-medium md:w-[220px]">
                  <SelectValue placeholder="Select a site" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`property-option-${p.name}`}>
                      <span className="flex items-center gap-2">
                        {p.display_name}
                        {p.status === "new" && <Badge variant="info">New</Badge>}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <ThemeControl {...dashboardTheme} />
            <AlertsBell onOpenSite={openSite} />
            <AccountMenu
              isDemo={isDemo}
              onOpenSettings={() => setSettingsOpen(true)}
              onOpenSetup={() => setSetupOpen(true)}
              onDisconnect={handleDisconnect}
            />
          </div>
        </div>
      </header>

      {/* Dialogs live outside the menu: a Radix dialog trigger nested in a
          dropdown unmounts with the menu and never opens. */}
      {sharing.dialog}
      <SettingsModal siteId={selected} isDemo={isDemo} onConsentChange={() => { setInsights([]); setGeneratedAt(null); }} open={settingsOpen} onOpenChange={setSettingsOpen} model={model} onModelChange={handleModelChange} />
      <SetupGuide open={setupOpen} onOpenChange={setSetupOpen} />

      <main id="dashboard-main" className="mx-auto max-w-7xl px-4 pt-8 lg:px-6">
        {isDemo && (
          <div data-testid="demo-banner" className="mb-6 flex flex-col justify-between gap-3 rounded-card border border-emerald-100 bg-emerald-50 px-5 py-3.5 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3 text-slate-600">
              <PlayCircle size={20} className="shrink-0 text-emerald-700" aria-hidden="true" />
              <span className="text-small"><span className="font-semibold text-navy-950">You're exploring a sample demo</span> with a fictional site's Search Console + GA4 data. Explanations are fixed examples, not live AI answers. No AI provider key is used.</span>
            </div>
            <Button
              variant="primary"
              size="control"
              onClick={handleLogout}
              data-testid="demo-connect-real-button"
              className="shrink-0 whitespace-nowrap"
            >
              Connect your own site <ArrowRight size={15} aria-hidden="true" />
            </Button>
          </div>
        )}
        {error && (
          <div data-testid="data-error" className="mb-6 flex flex-col gap-3 rounded-card border border-amber-500/30 bg-amber-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <span className="flex items-center gap-3 text-small font-medium text-amber-700">
              <AlertCircle size={20} className="shrink-0" aria-hidden="true" /> {error}
            </span>
            <Button variant="quiet" size="control" onClick={() => setAttempt((a) => a + 1)} data-testid="data-error-retry" className="shrink-0">
              <RotateCcw size={15} aria-hidden="true" /> Try again
            </Button>
          </div>
        )}

        {propertiesLoading && <section role="status" data-testid="properties-loading"><h1>Loading your sites…</h1></section>}
        {propertiesError && <section role="alert" data-testid="properties-error" className="rounded-card border border-line bg-white p-6">
          <h1 className="font-heading text-h2-compact">We couldn't load your sites</h1>
          <p className="mt-3">You're still signed in. Try again in a moment, or review your Google connection.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button onClick={() => setPropertiesAttempt(a => a + 1)} data-testid="properties-retry">Try again</Button>
            <Button variant="quiet" onClick={() => setSetupOpen(true)}>Set up Google</Button>
          </div>
        </section>}
        {!propertiesLoading && !propertiesError && <>
        {/* Shared toolbar: breadcrumb left, range right. */}
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2 text-small">
            {view === "site" && properties.length > 1 ? (
              <button type="button" onClick={() => setView("overview")} data-testid="breadcrumb-all-sites" className="tap-safe inline-flex items-center gap-1.5 pr-1 font-semibold text-slate-600 transition-colors duration-control hover:text-navy-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600">
                <LayoutGrid size={14} aria-hidden="true" /> All sites
              </button>
            ) : (
              <span className="inline-flex items-center gap-1.5 font-semibold text-slate-600"><LayoutGrid size={14} aria-hidden="true" /> All connected sites</span>
            )}
            {view === "site" && meta && (
              <><span className="text-slate-500" aria-hidden="true">/</span><span className="font-semibold text-navy-950">{meta.display_name}</span></>
            )}
          </div>
          <RangePicker value={range} onChange={setRange} />
        </div>

        {view === "overview" ? (
          <Overview range={range} onOpenSite={openSite} onConnect={() => setSetupOpen(true)} />
        ) : (
        <>
        {/* ---------- Top 3 opportunities ---------- */}
        {workflowEnabled && selected ? <section id="priorities"><OpportunityBank key={selected} siteId={selected} request={workflowRequest} /></section> : <section id="priorities" data-testid="insights-hero">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <h1 className="mt-2 font-heading text-h1-sm text-navy-950 lg:text-h1-app">
                Know what to fix next.
              </h1>
              <p className="mt-3 max-w-2xl text-body text-slate-600">
                {meta ? <>Up to three priorities from the available evidence for <span className="font-semibold text-navy-950">{meta.display_name}</span>. Start with one. Inspect the evidence when you need more context.</> : "Loading your website…"}
              </p>
              {/*
                Model and generation time sit here as read-only text. The model
                used to be a segmented control in the toolbar, which gave a
                once-a-month setting the same weight as the date range.
              */}
              {generatedAt && (
                <p className="mt-1.5 text-caption text-slate-600" data-testid="insights-meta">
                  {MODEL_LABEL[model] || MODEL_LABEL.claude} · Updated{" "}
                  <span className="tabular">
                    {new Date(generatedAt).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}
                  </span>
                </p>
              )}
            </div>
            <Button
              variant="quiet"
              size="control"
              onClick={handleRefresh}
              disabled={refreshing || loadingInsights}
              loading={refreshing}
              data-testid="refresh-insights-button"
              className="shrink-0"
            >
              {!refreshing && <RefreshCw size={16} aria-hidden="true" />}
              <span>Refresh</span>
            </Button>
          </div>

          {isNew && (
            <div data-testid="onboarding-empty-state" className="mt-6 flex items-start gap-4 rounded-card border border-emerald-100 bg-emerald-50 p-5 md:p-6">
              <Rocket size={20} className="mt-0.5 shrink-0 text-emerald-700" aria-hidden="true" />
              <div>
                <h3 className="font-heading text-h3 text-navy-950">You've just connected {meta?.display_name || "this site"} — nice one</h3>
                <p className="mt-1.5 max-w-2xl text-small text-slate-600">
                  This site was recently connected to SEO Signals. Connection age is not the age of your Google data. Review the reporting dates and available evidence before choosing a fix; a recent connection alone does not mean there is too little data.
                </p>
              </div>
            </div>
          )}

          <div className="ss-priority-grid mt-8 grid auto-rows-fr gap-5 lg:grid-cols-3">
            {showInsightsSkeleton ? (
              // Skeleton height matches a rendered card, so nothing jumps when
              // the real content lands.
              [0, 1, 2].map((i) => (
                <div key={i} data-testid="insight-skeleton" className="h-[320px] animate-pulse rounded-card border border-line bg-white" />
              ))
            ) : shownInsights.length > 0 ? (
              shownInsights.map((ins, i) => (
                <InsightCard
                  key={i} insight={ins} index={i}
                  onTrack={(insight) => handleTrackFix(insight, i)}
                  tracked={fixes.some((f) => f.headline === ins.headline)}
                  tracking={trackingIdx === i}
                />
              ))
            ) : (
              <div data-testid="insights-empty" className="rounded-card border border-line bg-white p-10 text-center lg:col-span-3">
                <Info size={24} className="mx-auto text-slate-500" aria-hidden="true" />
                <p className="mt-3 text-body text-slate-600">
                  Not enough data yet to surface opportunities. Check back once Google has gathered a few more days.
                </p>
              </div>
            )}
          </div>
        </section>}

        <section id="performance" className="ss-section">
          <h2>Your website, at a glance.</h2>
          <p>{reportingPeriod(data)} · Search Console. Clicks on search results are not measured website visits.</p>
          <div className="ss-metric-strip">
            <StatTile icon={MousePointerClick} label="Clicks from Google" value={formatMetric(gsc?.unavailable ? null : gsc?.totals?.clicks)} />
            <StatTile icon={Eye} label="Search impressions" value={formatMetric(gsc?.unavailable ? null : gsc?.totals?.impressions)} />
            <StatTile icon={Percent} label="Click-through rate" value={formatMetric(gsc?.unavailable ? null : gsc?.totals?.ctr, "%")} />
            <StatTile icon={TrendingUp} label="Average position" value={formatMetric(gsc?.unavailable ? null : gsc?.totals?.position)} sub="Aggregated across searches" />
          </div>
        </section>

        {/* ---------- Charts ---------- */}
        {showChartsSkeleton ? (
          <div className="mt-8 grid gap-5 md:mt-12 md:grid-cols-2">
            {[0, 1, 2, 3].map((i) => <div key={i} data-testid="chart-skeleton" className="h-[340px] animate-pulse rounded-card border border-line bg-white" />)}
          </div>
        ) : gsc && ga4 ? (
          <>
            {/* Search Console */}
            <div className="mt-8 md:mt-12">
              <SectionHeading>Search Console</SectionHeading>
            </div>
            {gsc.unavailable && <p role="status" className="mt-4 rounded-card bg-amber-50 p-4 text-small text-amber-700">Search Console data is temporarily unavailable. Check your Google connection and try again.</p>}
            <div className="mt-4 grid gap-5 lg:grid-cols-2">
              <ChartCard
                testid="gsc-trend-card"
                title="Clicks & impressions"
                explainer={gsc.unavailable ? "Search Console data is unavailable." : "Search Console · Impressions count appearances in search. Clicks count search-result clicks. Choose a metric below to inspect the daily observations."}
              >
                <ClicksImpressionsTrend trend={gsc.trend} />
              </ChartCard>
              <ChartCard
                testid="gsc-indexing-card"
                title="Indexing status"
                explainer="Search Console · Review index coverage separately from search performance. API coverage may be unavailable."
              >
                <IndexingPanel indexing={shownIndexing} />
              </ChartCard>
              <ChartCard
                testid="gsc-queries-card"
                title="Top search queries"
                explainer="Search Console · Returned queries, not all searches. Use clicks, impressions and average position together."
              >
                <DataTable
                  testid="queries-table"
                  emptyMessage="No queries in this range yet."
                  columns={[
                    { key: "query", label: "Query", wide: true },
                    { key: "clicks", label: "Clicks", align: "right" },
                    { key: "impressions", label: "Impr.", align: "right", render: (v) => formatMetric(v) },
                    { key: "ctr", label: "CTR", align: "right", render: (v) => <span className={v < 1 ? "font-semibold text-amber-700" : ""}>{formatMetric(v, "%")}</span> },
                    { key: "position", label: "Pos.", align: "right" },
                  ]}
                  rows={gsc.top_queries}
                />
              </ChartCard>
              <ChartCard
                testid="gsc-pages-card"
                title="Top pages"
                explainer="Search Console · Pages returned for this range. Low CTR alone does not identify the cause. Inspect page evidence below."
              >
                <DataTable
                  testid="pages-table"
                  emptyMessage="No pages in this range yet."
                  columns={[
                    { key: "page", label: "Page", mono: true, wide: true },
                    { key: "clicks", label: "Clicks", align: "right" },
                    { key: "ctr", label: "CTR", align: "right", render: (v) => <span className={v < 1 ? "font-semibold text-amber-700" : ""}>{formatMetric(v, "%")}</span> },
                    { key: "position", label: "Pos.", align: "right" },
                  ]}
                  rows={gsc.top_pages}
                />
              </ChartCard>
            </div>

            {/* GA4 */}
            <div className="mt-8 md:mt-12">
              <SectionHeading
                right={ga4Options.length > 0 ? (
                  <div className="flex items-center gap-2" data-testid="ga4-picker">
                    <span className="hidden text-caption font-semibold uppercase tracking-[0.08em] text-slate-600 sm:inline">GA4 property</span>
                    <Select
                      value={data?.property?.ga4_property || ""}
                      onValueChange={handleSetGa4}
                      disabled={savingGa4}
                    >
                      <SelectTrigger data-testid="ga4-property-select" className="tap-safe w-[220px] border-line bg-white text-small">
                        {savingGa4 ? <span className="flex items-center gap-2 text-slate-600"><Loader2 size={14} className="animate-spin" aria-hidden="true" /> Updating…</span> : <SelectValue placeholder="Pick a GA4 property" />}
                      </SelectTrigger>
                      <SelectContent>
                        {ga4Options.map((o) => (
                          <SelectItem key={o.id} value={o.id} data-testid={`ga4-option-${o.id}`}>{o.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
              >
                Analytics (GA4)
              </SectionHeading>
            </div>
            {ga4Demo && (
              <p className="mt-2 text-caption text-slate-600">In demo mode the analytics figures stay fixed — this shows how you'd re-pair a GA4 property on your real account.</p>
            )}
            {ga4.unavailable && (
              <div data-testid="ga4-unavailable" className="mt-4 flex items-start gap-3 rounded-card border border-amber-500/30 bg-amber-50 px-5 py-4 text-amber-700">
                <AlertCircle size={20} className="mt-0.5 shrink-0" aria-hidden="true" />
                <span className="text-small">Analytics data is unavailable. Choose a GA4 property you can access, or reconnect Google and try again.</span>
              </div>
            )}
            <div className="mt-4 grid gap-5 lg:grid-cols-2">
              <ChartCard
                testid="ga4-trend-card"
                title="Traffic trend"
                explainer="GA4 · Active users and sessions across the selected Analytics property and all traffic channels. Sessions are not search clicks."
              >
                <TrafficTrend trend={ga4.trend} optionalAvailable={reportAvailable(ga4, "engagement_trend")} />
              </ChartCard>
              <ChartCard
                testid="ga4-conversions-card"
                title="Key events"
                explainer={ga4.unavailable ? "Analytics data is unavailable." : `GA4 · Configured key events. ${formatMetric(ga4.totals?.conversion_rate)} key events per 100 sessions. This ratio can exceed 100 and is not a conversion probability.`}
              >
                <ConversionBars events={ga4.conversion_events} />
              </ChartCard>
              <ChartCard
                testid="ga4-landing-card"
                title="Top landing pages"
                explainer="GA4 · Sessions starting on these paths across the selected Analytics property. Host identity is shown when available. Key events per 100 sessions is a count ratio, not a probability."
              >
                <DataTable
                  testid="landing-table"
                  emptyMessage="No landing-page data in this range yet."
                  columns={[
                    { key: "page", label: "Page", mono: true, wide: true },
                    { key: "sessions", label: "Sessions", align: "right", render: (v) => formatMetric(v) },
                    { key: "hostname", label: "Hostname" },
                    { key: "conversions", label: "Key events", align: "right" },
                    { key: "conversion_rate", label: "Events / 100 sessions", align: "right", render: (v) => formatMetric(v) },
                  ]}
                  rows={ga4.top_landing_pages}
                />
              </ChartCard>
              <ChartCard title="Engagement context" testid="ga4-engagement-card" explainer="GA4 · Selected property, all traffic channels. Engagement measures active engagement rather than elapsed time on page.">
                <dl className="ss-detail-metrics">
                  <div><dt>Engaged sessions</dt><dd>{formatMetric(reportAvailable(ga4, "engagement_totals") ? ga4.totals?.engaged_sessions : null)}</dd></div>
                  <div><dt>Engagement rate</dt><dd>{formatMetric(reportAvailable(ga4, "engagement_totals") ? ga4.totals?.engagement_rate : null, "%")}</dd></div>
                  <div><dt>Engagement / active user</dt><dd>{formatMetric(reportAvailable(ga4, "engagement_totals") ? ga4.totals?.avg_engagement_sec : null, "s")}</dd></div>
                </dl>
                {!reportAvailable(ga4, "engagement_totals") && <p>Engagement report unavailable. Traffic and key-event reports remain separate.</p>}
              </ChartCard>
            </div>
          </>
        ) : null}
        {!showChartsSkeleton && data && <DashboardReports isDemo={isDemo} key={`${selected}:${range}`} data={data} findings={[...(data?.opportunity_insights || []), ...insights, ...shownQuickWins]} onTrack={insight => handleTrackFix(insight, "page")} fixes={fixes} tracking={trackingIdx !== null} />}
        <section id="progress" className="ss-section"><h2>One step at a time.</h2><p>Your tracked fixes. Completion records your work, not a verified improvement in search performance.</p>
        {fixes.length === 0 && <p className="ss-empty-progress">No tracked fixes yet. Use “Track this fix” on a priority to begin.</p>}
        <FixTracker
          fixes={fixes}
          onToggleDone={handleToggleDone}
          onRemove={handleRemoveFix}
          busyId={fixBusyId}
        />

        {!isNew && (
          <div className="mt-8 md:mt-12">
            <QuickWinsTable rows={shownQuickWins} loading={loadingData || fixture === "loading"} />
          </div>
        )}

        </section>
        {selected && <ClarityReport propertyId={selected} range={range} isDemo={isDemo} gscPages={data?.gsc?.top_pages} onStatus={setClarityStatus} />}
        {selected && <BingReport propertyId={selected} range={range} isDemo={isDemo} siteName={meta?.display_name} onStatus={setBingStatus} />}
        <section id="sources" className="ss-section ss-sources"><h2>Your data sources</h2>
          <dl><div><dt>Google Search Console</dt><dd>{loadingData ? "Loading" : gsc?.unavailable || !gsc ? "Unavailable" : isDemo ? "Demo data" : "Report returned"}</dd></div>
          <div><dt>Google Analytics</dt><dd>{loadingData ? "Loading" : ga4?.unavailable || !ga4 ? "Unavailable" : isDemo ? "Demo data" : "Selected GA4 property"}</dd></div>
          <div><dt>Microsoft Clarity</dt><dd data-testid="clarity-source-status">{clarityStatus}</dd></div>
          <div><dt>Bing Webmaster Tools</dt><dd data-testid="bing-source-status">{bingStatus}</dd></div></dl>
          <p>{reportingPeriod(data)}. These live reports do not establish complete-site crawl or per-page history.</p>
          {!isDemo && selected && <HistorySources siteId={selected} />}
        </section>
        </>
        )}
        </>}
      </main>

      </div>
      {view === "site" && selected && meta && <ChatPanel isDemo={isDemo} propertyId={selected} propertyName={meta.display_name} range={range} model={model} />}
    </div>
  );
}
